// Shared constants for the support chat's funnel mode (cca documentation/
// 52-support-chat-funnel-bot.md). Kept out of the provider so the widget, the
// teaser and the tests import them without pulling the WebSocket state along.

import log from "loglevel";

import { recordEvent } from "components/Analytics";
import { isBrowser } from "components/Browser";

// The fixed message the pricing-page teaser sends when tapped. The backend prompt
// names this exact string as a funnel entry (cca/support/funnel.py,
// FUNNEL_ENTRY_MESSAGE) -- change them together.
export const FUNNEL_ENTRY_MESSAGE = "I'd like help choosing a plan";

// The persona. A named human persona as in the prototype and in Warmly, plus the
// "AI assistant" label, which is not optional: a bot that nudges toward a purchase
// discloses that it is a bot (California Bus. & Prof. Code 17940-17943 covers bots
// that incentivize a purchase). The initials avatar stands in until there are photos.
//
// The name works in shifts. Time is cut into fixed shifts of PERSONA_SHIFT_MS from
// the epoch, and the name on duty in a shift is the next one round the list, so at
// any moment every visitor is greeted by the same team member and nobody is on
// duty around the clock. This replaced a pick-once-per-browser design, under which
// a returning visitor met the same name on every visit ("Elise can't be online
// 24/7"). With three shifts a day and five names the cycle is 40 hours, so a
// visitor who comes back at the same hour each day meets a different name for
// five days running (3 and 5 are coprime). The rota is frontend-only: the backend
// never sees which name a visitor was shown.
//
// A conversation keeps the name that greeted it (the provider stores it with the
// thread), so a reload or a shift change mid-chat never swaps the name under the
// visitor. A thread the visitor has not touched, and a cleared thread, are greeted
// by whoever is on duty now.
export const PERSONA_NAMES = ["Blake", "Aris", "Adam", "Elise", "Clara"];
export const PERSONA_ROLE = "Client Support Team";
export const PERSONA_SHIFT_MS = 8 * 60 * 60 * 1000;

export const personaNamed = (name) => ({ name, role: PERSONA_ROLE, initials: name[0] });

// The team member on duty at `now` (epoch ms). Pure: same instant, same name,
// on the server and in every browser.
export const personaOnShift = (now = Date.now()) =>
  personaNamed(PERSONA_NAMES[Math.floor(now / PERSONA_SHIFT_MS) % PERSONA_NAMES.length]);

// The prototype's opener question, with its intent chips. The first four are the
// funnel entry, answered by the backend's SCRIPT rather than the model
// (cca/support/scripted.py): a tap sends the label as the visitor's message plus
// `answer` as the payload's funnel_answer, and the reply to it is fixed. Steps and
// values are the backend's (STEP_USE_CASE, USE_CASES) -- change them together.
// The last chip is the visitor who wants support, not a plan: it carries no
// answer, so it goes to the model and routes into the support behaviour unchanged.
// It says "question", not "issue": the chat is also where people ask how the
// product works, and "issue" read as bug-reports-only.
//
// Why a script: with the model driving every turn the same two taps gave a
// different conversation each time -- "Building a product" was sometimes asked
// "personal or commercial?" and then told products are commercial-only.
export const FUNNEL_STEP_USE_CASE = "use_case";
export const FUNNEL_STEP_EMAIL = "email";

// The opener is two bubbles. The first says who this is for, in one line that
// a visitor with a real need recognises themselves in (review of 2026-09-25,
// point 14; the widget sets it a size larger and semibold, see
// .support-chat-message-opener). The second is the question the chips answer,
// as a follow-up bubble so the line above it stays clean.
export const OPENER_QUESTION = "What are you hoping to use earnings data for?";

export const openerGreeting = (persona) =>
  `Hi, I'm ${persona.name} from the EarningsCall team. You're in the right place if you're ` +
  "looking for an easy way to pull accurate, reliable earnings call data for your backtest, " +
  "research, AI agent, trading model, or whatever you're building.";

export const openerMessage = (persona = personaOnShift()) => ({
  role: "assistant",
  text: openerGreeting(persona),
  badges: [],
  attachments: [{ type: "followup", text: OPENER_QUESTION }],
});

const openerChip = (label, value) => ({
  label,
  message: label,
  answer: { step: FUNNEL_STEP_USE_CASE, value },
});

export const OPENER_CHIPS = [
  openerChip("Trading / backtesting", "trading"),
  openerChip("Research / exploring", "research"),
  openerChip("Academic", "academic"),
  openerChip("Building a product", "product"),
  { label: "I need help / have a question", message: "I need help with something" },
];

// A chip as the backend sends it (suggested_replies): a plain string (the Haiku
// chips, sent back verbatim as the visitor's message) or {label, step, value}
// (the funnel's, sent back as the label plus funnel_answer). Normalised to the
// widget's own shape so the rest of the code has one kind of chip.
export const chipFromWire = (chip) => {
  if (typeof chip === "string") {
    return chip;
  }
  return {
    label: chip.label,
    message: chip.label,
    answer: { step: chip.step, value: chip.value },
  };
};

// Shape check only, as in the prototype; real verification is not the widget's job.
export const isPlausibleEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// The pre-funnel widget copy, kept for the flag-off path (prod until the PostHog
// flag is ramped).
export const WELCOME_MESSAGE = {
  role: "assistant",
  text:
    "Hi! I can answer questions about EarningsCall — coverage, plans and " +
    "pricing, and how to use the API. What can I help with?",
  badges: [],
  attachments: [],
};

export const STARTER_SUGGESTIONS = [
  "How do I authenticate my API requests?",
  "Which plan is right for me?",
  "I have a billing issue",
];

// Where the proactive teaser may show (phase 3). Route-scoped on purpose: the widget
// renders on every page, and Warmly-style open-on-arrival would be a site-wide
// interruption. Matched with and without Gatsby's trailing slash.
export const TEASER_PATHS = [
  "/api-pricing",
  "/plan-finder",
  "/earnings-call-api",
  "/earnings-call-api-alternative",
  "/earnings-transcripts-api",
];

export const isTeaserPath = (pathname) => {
  const path = (pathname || "").replace(/\/+$/, "") || "/";
  return TEASER_PATHS.includes(path);
};

// The chat opens by itself shortly after a visitor lands (review of the funnel,
// 2026-09-22: "have this chatbox automatically pop up after 3 or 4 seconds when a
// visitor arrives", so visitors engage with it instead of browsing anonymously).
// Once per tab: the provider marks PANEL_OPENED_STORAGE_KEY on ANY open, so a
// visitor who opened and closed the chat themselves is never reopened on, and a
// reload does not pop it again. Desktop only: on a phone the panel is full-screen,
// and a full-screen takeover a few seconds after arrival is the "intrusive
// interstitial" Google demotes mobile pages for. Not on the pages where the
// visitor is already in the middle of something (checkout, login, sign-up,
// payment), and not for a signed-in visitor, who has already given an email.
export const AUTO_OPEN_DELAY_MS = 4_000;
export const PANEL_OPENED_STORAGE_KEY = "supportChatPanelOpened";
// A pricing-page plan click (api-pricing.js, onPlanSelected): the teaser must
// not fire over the login modal that click opens on the same page.
export const PLAN_SELECTED_STORAGE_KEY = "supportChatPlanSelected";
// The reset time of the spent quota window this tab has already counted.
export const QUOTA_REPORTED_STORAGE_KEY = "supportChatQuotaReported";

// sessionStorage can throw (a SecurityError under some privacy settings and in
// embedded contexts). The marks are kept in memory for the life of the page as
// well, so an unavailable store neither crashes the widget nor defeats the
// once-per-tab guarantees (a closed auto-open must not open again, a dismissed
// teaser must not come back); only a full reload forgets them then.
const marksInMemory = new Set();
export const readSessionMark = (key) => {
  if (marksInMemory.has(key)) {
    return true;
  }
  try {
    return isBrowser() && sessionStorage.getItem(key) === "true";
  } catch (error) {
    return false;
  }
};
// Fired on window whenever a mark is written, with the key as `detail`: a
// component that reads marks at render (the teaser's eligibility) re-reads
// them on it, so a plan clicked while the teaser is already up hides it.
export const SESSION_MARK_EVENT = "support-chat:session-mark";

export const writeSessionMark = (key) => {
  marksInMemory.add(key);
  try {
    if (isBrowser()) {
      sessionStorage.setItem(key, "true");
    }
  } catch (error) {
    // Kept in memory above.
  }
  if (isBrowser()) {
    window.dispatchEvent(new CustomEvent(SESSION_MARK_EVENT, { detail: key }));
  }
};
// A mark with a value (a reset time, a link key), same storage and fallback.
const valuesInMemory = new Map();
export const readSessionValue = (key) => {
  if (valuesInMemory.has(key)) {
    return valuesInMemory.get(key);
  }
  try {
    return isBrowser() ? sessionStorage.getItem(key) : null;
  } catch (error) {
    return null;
  }
};
export const writeSessionValue = (key, value) => {
  valuesInMemory.set(key, value);
  try {
    if (isBrowser()) {
      sessionStorage.setItem(key, value);
    }
  } catch (error) {
    // Kept in memory above.
  }
};
// For tests: a fresh tab.
export const forgetSessionMarks = () => {
  marksInMemory.clear();
  valuesInMemory.clear();
};
export const wasPanelOpenedThisSession = () => readSessionMark(PANEL_OPENED_STORAGE_KEY);
export const markPlanSelected = () => writeSessionMark(PLAN_SELECTED_STORAGE_KEY);
export const wasPlanSelectedThisSession = () => readSessionMark(PLAN_SELECTED_STORAGE_KEY);
const AUTO_OPEN_EXCLUDED_PATHS = [
  "/quiz",
  "/checkout",
  "/checkout-square",
  "/login",
  "/signup",
  "/signup-email",
  "/post-signup",
  "/one-more-step",
  "/confirm-email",
  "/mail-confirmation",
  "/reset-password",
  "/payment-info",
  "/payment-success",
  "/delete-account",
  "/admin",
];

export const isAutoOpenPath = (pathname) => {
  const path = (pathname || "").replace(/\/+$/, "") || "/";
  return !AUTO_OPEN_EXCLUDED_PATHS.some(
    (excluded) => path === excluded || path.startsWith(`${excluded}/`)
  );
};

// Every widget event goes through recordEvent so GA4, PostHog and the ES analytics
// index all get it (components/Analytics.js). Names are the ones in the design doc.
export const trackSupportChat = (name, properties = {}) => {
  // Best-effort: recordEvent queues through localStorage (components/Analytics.js),
  // which can throw where storage is blocked, and an event must never abort the
  // chat action it describes (a send, a click, an open).
  try {
    recordEvent({ name, ...properties });
  } catch (error) {
    log.warn("support-chat: analytics event dropped", name, error);
  }
};
